import { useCallback, useEffect, useState } from 'react'
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router'
import SignIn from './pages/Authentication/SignIn'
import { AuthContext } from './AuthProvider.ts'
import { type IdTokenClaims, useHandleSignInCallback, useLogto } from '@logto/react'
import * as Sentry from '@sentry/react'
import OrganizationAuth from './OrganizationAuth.tsx'
import FullPageLoader from './common/FullPageLoader'
import ConfirmInvite from './pages/Authentication/ConfirmInvite.tsx'
import { useOrganizationsByToken } from './http/hooks.ts'
import { ActiveOrganizationAuth } from './auth/types.ts'
import { NextPeakClientProvider } from './services/nextpeakClient/clientProvider.tsx'
import { NextPeakPublicClientProvider } from './services/nextpeakPublicClient/clientProvider.tsx'
import { RedirectOnSignedOut } from './pages/Authentication/RedirectOnSignedOut.tsx'

/**
 * Any authentication business logic related to external auth service should be handled here.
 * @constructor
 */
function Authenticator() {
	const [, setSearchParams] = useSearchParams()
	const navigate = useNavigate()

	const { isLoading } = useHandleSignInCallback(() => {
		const redirectUrl = localStorage.getItem('postLoginRedirect')
		if (redirectUrl) {
			navigate(redirectUrl)
			localStorage.removeItem('postLoginRedirect')
		} else {
			setSearchParams({})
		}
	})

	const { isAuthenticated, getAccessToken, getIdTokenClaims, getOrganizationTokenClaims, getOrganizationToken, getAccessTokenClaims } =
		useLogto()
	const [user, setUser] = useState<IdTokenClaims>()
	const [token, setToken] = useState<string>()
	const [userOrganizations, setUserOrganizations] = useState<any[]>([])
	const [activeOrganizationAuth, setActiveOrganizationAuth] = useState<ActiveOrganizationAuth>()
	const [organizationToken, setOrganizationToken] = useState<string>()

	const { data: userOrgsFromApi, isLoading: isOrganizationsLoading } = useOrganizationsByToken(token)

	const handleToken = useCallback(async () => {
		if (isAuthenticated) {
			const claims = await getIdTokenClaims()
			setUser(claims)
			const accessToken = await getAccessToken(import.meta.env.VITE_LOGTO_API_URL)
			const accessTokenClaims = await getAccessTokenClaims(import.meta.env.VITE_LOGTO_API_URL)
			if (accessToken && accessToken !== token) {
				setToken(accessToken)
				localStorage.setItem('accessToken', accessToken)
			}
			if (accessTokenClaims?.exp) localStorage.setItem('accessTokenExpiresAt', accessTokenClaims.exp.toString())
		}
	}, [isAuthenticated, getIdTokenClaims, getAccessToken, token])

	const handleOrganizationChange = useCallback(
		async (orgId: string, organizations?: { id: string; name: string }[]) => {
			if (organizations) {
				const org = organizations.find((org) => org.id === orgId)
				if (!org) {
					localStorage.removeItem('organizationId')
					throw new Error('Attempting to authenticate with an organization that user is not a part of')
				}
				await handleOrganizationTokenRefresh(org)
			}
		},
		[getOrganizationToken, setOrganizationToken]
	)

	const handleOrganizationTokenRefresh = useCallback(
		async (org: { id: string; name: string }) => {
			const organizationClaims = await getOrganizationTokenClaims(org.id)
			setActiveOrganizationAuth({
				...organizationClaims,
				orgId: org.id,
				orgName: org.name,
			})
			const orgToken = await getOrganizationToken(org.id)
			if (orgToken) {
				setOrganizationToken(orgToken)
				localStorage.setItem('organizationToken', orgToken)
				localStorage.setItem('organizationId', org.id)
			}
			if (organizationClaims?.exp) {
				localStorage.setItem('organizationTokenExpiresAt', organizationClaims.exp.toString())
			}
		},
		[getOrganizationToken, getOrganizationTokenClaims]
	)

	const handleAllTokensRefresh = useCallback(async () => {
		await handleToken()
		if (activeOrganizationAuth) {
			await handleOrganizationTokenRefresh({
				id: activeOrganizationAuth.orgId,
				name: activeOrganizationAuth.orgName,
			})
		}
	}, [handleToken, handleOrganizationTokenRefresh, activeOrganizationAuth])

	const logOutOrganization = useCallback(() => {
		setActiveOrganizationAuth(undefined)
		setOrganizationToken('')
		localStorage.removeItem('organizationToken')
		localStorage.removeItem('organizationId')
		localStorage.removeItem('organizationTokenExpiresAt')
	}, [])

	useEffect(() => {
		handleToken()
	}, [isAuthenticated, getIdTokenClaims, getAccessToken, token, handleToken])

	useEffect(() => {
		if (userOrgsFromApi) {
			setUserOrganizations(userOrgsFromApi)

			const activeOrganizationId = localStorage.getItem('organizationId')
			if (activeOrganizationId) {
				handleOrganizationChange(activeOrganizationId, userOrgsFromApi)
			} else {
				if (userOrgsFromApi.length === 1) {
					handleOrganizationChange(userOrgsFromApi[0].id, userOrgsFromApi)
				}
			}
		}
	}, [userOrgsFromApi])

	const { pathname } = useLocation()

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	if (isLoading || isOrganizationsLoading) {
		return <FullPageLoader />
	}

	if (!isAuthenticated || !token || !user) {
		return (
			<NextPeakPublicClientProvider>
				<Routes>
					<Route index element={<SignIn />} />
					<Route path="/invites/:inviteId/confirm" element={<ConfirmInvite />} />
					<Route path="*" element={<RedirectOnSignedOut />} />
				</Routes>
			</NextPeakPublicClientProvider>
		)
	}

	return (
		<AuthContext.Provider value={{ token, authUser: user, refreshAuth: handleAllTokensRefresh }}>
			<NextPeakClientProvider>
				<OrganizationAuth
					handleOrganizationChange={handleOrganizationChange}
					activeOrganizationAuth={activeOrganizationAuth}
					organizationToken={organizationToken}
					userOrganizations={userOrganizations}
					logOutOrganization={logOutOrganization}
					handleOrganizationTokenRefresh={handleOrganizationTokenRefresh}
				/>
			</NextPeakClientProvider>
		</AuthContext.Provider>
	)
}

//export default App
export default Sentry.withProfiler(Authenticator)
